import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import '../styles/Auth.css';

const Register = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [admitCardFile, setAdmitCardFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!username || !email || !password || !admitCardFile) {
      return setError('All fields including admit card image are required!');
    }

    setError('');
    setLoading(true);

    try {
      // Step 1: Detect face from admit card
      const admitValid = await handleAdmitCardUpload({ target: { files: [admitCardFile] } });
      if (!admitValid) {
        setError("No face detected in admit card.");
        setLoading(false);
        return;
      }

      // Step 2: Capture and verify face live
      const liveValid = await captureAndUploadFace(email);
      if (!liveValid) {
        setError("No face detected or spoof detected in live webcam.");
        setLoading(false);
        return;
      }

      // Step 3: Firebase register
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: username });
      console.log("✅ Firebase registration success");

      // 🔑 Save token so user stays logged in
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('token', token);

      alert('🎉 Registration complete!');
      navigate('/login'); 
    } catch (err) {
      console.error("❌ Registration Failed:", err.message);
      setError(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const captureAndUploadFace = async (email) => {
    const video = document.createElement('video');
    video.style.display = 'none';
    document.body.appendChild(video);

    try {
      const faceapi = window.faceapi;
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      video.srcObject = stream;

      return new Promise((resolve) => {
        video.onloadedmetadata = async () => {
          video.play();

          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const detections = await faceapi.detectAllFaces(canvas, new faceapi.TinyFaceDetectorOptions());
          if (detections.length === 0) {
            alert("❌ No face detected. Please try again.");
            stream.getTracks().forEach((track) => track.stop());
            video.remove();
            return resolve(false);
          }

          canvas.toBlob(async (blob) => {
            const verifyForm = new FormData();
            verifyForm.append('email', email);
            verifyForm.append('live_image', blob, `${email}_live.jpg`);

            const verifyRes = await fetch(`${process.env.REACT_APP_API_URL}/api/verify-spoof-only`, {
              method: 'POST',
              body: verifyForm,
              credentials: 'include'
            });

            const verifyResult = await verifyRes.json();
            console.log("🧠 Spoof Check Result:", verifyResult);

            if (!verifyResult.spoof) {
              alert("❌ Registration blocked. Real face not detected.");
              stream.getTracks().forEach((track) => track.stop());
              video.remove();
              return resolve(false);
            }

            const formData = new FormData();
            formData.append('email', email);
            formData.append('live_image', blob, `${email}_live.jpg`);
            formData.append('admit_card', admitCardFile);

            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/register-face`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
              });

              const result = await response.json();
              if (result.success) {
                console.log("✅ Face and Admit Card uploaded successfully.");
                resolve(true);
              } else {
                console.error("❌ Upload failed:", result.message);
                alert("Face/Admit card upload failed.");
                resolve(false);
              }
            } catch (err) {
              console.error("❌ Upload error:", err.message);
              alert("Server error. Try again later.");
              resolve(false);
            } finally {
              stream.getTracks().forEach((track) => track.stop());
              video.remove();
            }
          }, 'image/jpeg');
        };
      });
    } catch (err) {
      alert("❌ Could not access webcam or detect face.");
      console.error(err);
      video.remove();
      return false;
    }
  };

  const handleAdmitCardUpload = async (e) => {
    const faceapi = window.faceapi;
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      alert("❌ Please upload a valid image file.");
      return false;
    }

    await faceapi.nets.tinyFaceDetector.loadFromUri('/models');

    const img = await faceapi.bufferToImage(file);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const detections = await faceapi.detectAllFaces(canvas, new faceapi.TinyFaceDetectorOptions());
    if (detections.length === 0) {
      alert("❌ No face detected in the admit card image.");
      return false;
    }

    setAdmitCardFile(file);
    return true;
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <h1 className="project-title">Examify</h1>
        <p className="quote">"Empower Your Knowledge, Elevate Your Success"</p>

        <h2>Register</h2>
        {error && <p style={{ color: 'red' }}>{error}</p>}

        <form className="auth-form" onSubmit={handleRegister}>
          <input
            type="text"
            placeholder="Username"
            className="auth-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
          />
          <input
            type="email"
            placeholder="Email"
            className="auth-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Password"
            className="auth-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <label style={{ marginTop: '10px', color: '#fff' }}>
            Upload Admit Card Image
          </label>
          <input
            type="file"
            accept="image/*"
            className="auth-input"
            onChange={handleAdmitCardUpload}
            disabled={loading}
          />
          <button className="auth-button" type="submit" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <p className="auth-switch">
          Already have an account?{' '}
          <span
            onClick={() => navigate('/login')}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Login here
          </span>
        </p>
      </div>
    </div>
  );
};

export default Register;
